/**
 * TRAIL PAGE - Precisely matches mockup screenshot
 * Dark header, stats grid, progress bar, filter pills, venue list
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { venuesAPI, roadsAPI } from '../api';
import { BottomNav } from './Home';
import VenueModal from '../components/VenueModal';
import QRScanner from '../components/QRScanner';
import LoadingSpinner from '../components/LoadingSpinner';

export default function Trail() {
    const [roads, setRoads] = useState([]);
    const [selectedRoad, setSelectedRoad] = useState(null);
    const [venues, setVenues] = useState([]);
    const [checkins, setCheckins] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    const [scanTarget, setScanTarget] = useState(null);
    const [checkinResult, setCheckinResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('All Stops');
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([roadsAPI.getAll(), venuesAPI.getUserCheckins()])
            .then(([roadsRes, checkinsRes]) => {
                setRoads(roadsRes.data);
                setCheckins(checkinsRes.data);
                
                const savedSlug = localStorage.getItem('selectedRoadSlug');
                const roadToSelect = savedSlug 
                    ? roadsRes.data.find(r => r.slug === savedSlug) || roadsRes.data[0]
                    : roadsRes.data[0];
                
                if (roadToSelect) {
                    setSelectedRoad(roadToSelect);
                }
            });
    }, []);

    useEffect(() => {
        if (!selectedRoad) return;
        setLoading(true);
        venuesAPI.getAll(selectedRoad.slug)
            .then(r => setVenues(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [selectedRoad]);

    const isCheckedIn = (venueId) => checkins.some(c => c.venue_id === venueId);
    const getCheckin = (venueId) => checkins.find(c => c.venue_id === venueId);

    const categories = ['All Stops', 'Distillery', 'Restaurant', 'Bar', 'Attraction', 'Lodging'];
    const filtered = filter === 'All Stops'
        ? venues
        : venues.filter(v => v.category?.toLowerCase() === filter.toLowerCase());

    const checkedCount = venues.filter(v => isCheckedIn(v.id)).length;
    const stopsRequired = selectedRoad?.stops_required || venues.length || 0;
    const leftCount = Math.max(0, stopsRequired - checkedCount);
    const pct = stopsRequired > 0 ? Math.min(100, Math.round((checkedCount / stopsRequired) * 100)) : 0;

    if (loading && !venues.length) return <LoadingSpinner />;

    return (
        <div style={s.screen}>
            {/* Dark Header */}
            <div style={s.header}>
                {/* Title Row */}
                <div style={s.headerTop}>
                    <div>
                        <h1 style={s.h1}>{selectedRoad?.name || 'The Copper Road'}</h1>
                        <div style={s.headerSub}>{selectedRoad?.region || 'Kentucky'} — All Stops</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={s.toggleGroup}>
                            <button style={{ ...s.toggleBtn, ...s.toggleActive }}>≡ List</button>
                            <button style={s.toggleBtn} onClick={() => navigate('/map')}>🗺️ Map</button>
                        </div>
                        <div
                            onClick={() => window.location.href = '/profile'}
                            style={{
                                width: 44, height: 44, borderRadius: '50%',
                                background: '#ffffff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                                cursor: 'pointer', border: '2px solid #c48f4a',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <span style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))' }}>👤</span>
                        </div>
                    </div>
                </div>

                {/* Road Selector Carousel added as requested */}
                <div style={s.roadSelector}>
                    {roads.map(r => (
                        <div 
                            key={r.id} 
                            onClick={() => {
                                setSelectedRoad(r);
                                localStorage.setItem('selectedRoadSlug', r.slug);
                            }}
                            style={{ 
                                ...s.roadPill, 
                                background: selectedRoad?.id === r.id ? '#b8753d' : 'rgba(255,255,255,0.1)',
                                border: selectedRoad?.id === r.id ? '1px solid #fff' : '1px solid transparent'
                            }}
                        >
                            {r.name}
                        </div>
                    ))}
                </div>

                {/* Stats grid - 3 boxes */}
                <div style={s.statsGrid}>
                    <div style={s.statBox}>
                        <div style={s.statNum}>{checkedCount}</div>
                        <div style={s.statLbl}>DONE</div>
                    </div>
                    <div style={s.statBox}>
                        <div style={s.statNum}>{leftCount}</div>
                        <div style={s.statLbl}>LEFT</div>
                    </div>
                    <div style={s.statBox}>
                        <div style={s.statNum}>{stopsRequired}</div>
                        <div style={s.statLbl}>REQ STOPS</div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div style={s.progressTrack}>
                    <div style={{ ...s.progressFill, width: `${pct}%` }} />
                </div>
                <div style={s.progressLabel}>{pct}% complete</div>
                {/* Scan CTA moved inside header */}
                <div style={s.scanWrap}>
                    <button style={s.scanBtn} onClick={() => setShowScanner(true)}>
                        📷 Scan a Venue Check-In
                    </button>
                </div>
            </div>

            {/* Filter pills */}
            <div style={s.filters}>
                {categories.map(cat => (
                    <button
                        key={cat}
                        type="button"
                        style={{ ...s.pill, ...(filter === cat ? s.pillActive : {}) }}
                        onClick={() => setFilter(cat)}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Venue List */}
            <div style={s.list}>
                {filtered.map((venue, index) => {
                    const checked = isCheckedIn(venue.id);
                    // Filtered list index may not correspond to actual total stop number if we had global ordering,
                    // but we will use the index array for the visual requirement.
                    return (
                        <div key={venue.id} style={s.card} onClick={() => setSelectedVenue(venue)}>
                            <div style={s.iconBox}>
                                <span>{venue.icon || '🥃'}</span>
                            </div>
                            <div style={s.info}>
                                <div style={s.venueStopNumber}>{selectedRoad?.name || 'Road'} · Stop {index + 1}</div>
                                <div style={s.venueName}>{venue.name}</div>
                                <div style={s.venueTagline}>{venue.tagline}</div>
                                <div style={s.venueMeta}>{venue.city} · <span style={s.catText}>{venue.category}</span></div>
                            </div>
                            <div style={s.statusCol}>
                                <div style={{ ...s.statusBadge, ...(checked ? s.badgeDone : s.badgePending) }}>
                                    {checked ? '✓' : '○'}
                                </div>
                                <button style={s.detailsBtn}>Details →</button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Modals */}
            {selectedVenue && (
                <VenueModal
                    venue={selectedVenue}
                    isCheckedIn={isCheckedIn(selectedVenue.id)}
                    checkinAt={getCheckin(selectedVenue.id)?.checkin_at}
                    onClose={() => setSelectedVenue(null)}
                    onScanQR={() => { setScanTarget(selectedVenue); setSelectedVenue(null); setShowScanner(true); }}
                />
            )}
            {showScanner && (
                <div style={s.scannerModal}>
                    <QRScanner
                        onScanSuccess={(token) => {
                            setShowScanner(false);
                            setLoading(true);
                            navigator.geolocation.getCurrentPosition(
                                async (pos) => {
                                    try {
                                        const res = await venuesAPI.submitCheckin(token, pos.coords.latitude, pos.coords.longitude);
                                        setCheckinResult({ success: true, message: res.data.message });
                                        venuesAPI.getUserCheckins().then(r => setCheckins(r.data));
                                    } catch (e) {
                                        setCheckinResult({ 
                                            success: false, 
                                            message: e.response?.data?.message || e.response?.data?.error || 'Check-in failed' 
                                        });
                                    } finally {
                                        setLoading(false);
                                    }
                                },
                                () => {
                                    setCheckinResult({ success: false, message: 'Location access required for check-in.' });
                                    setLoading(false);
                                },
                                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
                            );
                        }}
                        onClose={() => setShowScanner(false)}
                    />
                </div>
            )}
            {checkinResult && (
                <div style={{ ...s.toast, background: checkinResult.success ? '#2e7d32' : '#c62828' }}>
                    {checkinResult.message}
                    <button style={s.toastClose} onClick={() => setCheckinResult(null)}>✕</button>
                </div>
            )}

            <BottomNav active="/trail" />
        </div>
    );
}

const s = {
    screen: { background: '#f0ebe3', minHeight: '100vh', paddingBottom: 100 },
    loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 20, color: '#b8753d', background: '#1a2234' },

    // DARK HEADER
    header: { background: '#1a2234', padding: '56px 20px 24px', color: '#fff' },
    headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
    h1: { fontSize: 24, fontFamily: 'Georgia, serif', margin: 0, fontWeight: 'bold', color: '#fff' },
    headerSub: { fontSize: 13, color: '#8899aa', marginTop: 4 },

    // Toggle buttons
    toggleGroup: { display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 20, padding: 3 },
    toggleBtn: { background: 'none', border: 'none', color: '#fff', fontSize: 12, padding: '6px 12px', borderRadius: 16, cursor: 'pointer' },
    toggleActive: { background: '#fff', color: '#1a2234', fontWeight: 'bold' },

    // Stats grid - exactly as in screenshot
    statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 },
    statBox: { background: 'rgba(255,255,255,0.07)', borderRadius: 12, padding: '14px 8px', textAlign: 'center' },
    statNum: { fontSize: 22, fontWeight: 'bold', color: '#fff', fontFamily: 'Georgia, serif' },
    statLbl: { fontSize: 10, color: '#8899aa', fontWeight: '700', letterSpacing: 0.8, marginTop: 4 },

    // Progress
    progressTrack: { height: 5, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden', marginBottom: 8 },
    progressFill: { height: '100%', background: '#b8753d', borderRadius: 3, transition: 'width 0.5s' },
    progressLabel: { fontSize: 11, color: '#8899aa', textAlign: 'left' },

    // Scan button
    scanWrap: { padding: '20px 20px 0' },
    scanBtn: {
        width: '100%', background: 'linear-gradient(135deg, #c07840 0%, #8d5530 100%)',
        color: '#fff', border: 'none', borderRadius: 14, padding: '16px',
        fontSize: 15, fontWeight: 'bold', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        boxShadow: '0 4px 16px rgba(184,117,61,0.35)'
    },

    // Filters
    filters: { display: 'flex', gap: 8, padding: '16px 20px', overflowX: 'auto' },
    pill: {
        padding: '8px 16px', borderRadius: 20, border: '1px solid #d8ccbe',
        background: '#fff', color: '#888', fontSize: 12, cursor: 'pointer',
        whiteSpace: 'nowrap', fontWeight: '600', flexShrink: 0
    },
    pillActive: { background: '#b8753d', color: '#fff', border: '1px solid #b8753d' },

    // Venue list
    list: { padding: '4px 20px 40px' },
    card: {
        background: '#fff', borderRadius: 16, padding: '16px',
        display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', cursor: 'pointer'
    },
    iconBox: {
        width: 50, height: 50, background: '#f0f4f0', borderRadius: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, flexShrink: 0
    },
    info: { flex: 1, minWidth: 0 },
    venueStopNumber: { fontSize: 10, color: '#b8753d', fontWeight: '800', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 },
    venueName: { fontSize: 14, fontWeight: '700', color: '#1c1c1c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    venueTagline: { fontSize: 12, color: '#888', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    venueMeta: { fontSize: 11, color: '#555', marginTop: 4 },
    catText: { color: '#b8753d', fontWeight: '700', textTransform: 'lowercase' },

    roadSelector: { display: 'flex', gap: 10, overflowX: 'auto', padding: '0 0 20px', scrollbarWidth: 'none' },
    roadPill: { padding: '8px 16px', borderRadius: 20, color: '#fff', fontSize: 12, fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s' },

    statusCol: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 },
    statusBadge: { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 'bold' },
    badgeDone: { background: '#e8f5e9', color: '#4caf50', border: '1.5px solid #4caf50' },
    badgePending: { background: '#f5f5f5', color: '#aaa', border: '1.5px solid #ddd' },
    detailsBtn: { background: 'none', border: 'none', color: '#b8753d', fontSize: 11, fontWeight: '700', cursor: 'pointer', padding: 0 },

    toast: { position: 'fixed', bottom: 100, left: 20, right: 20, padding: 16, borderRadius: 12, color: '#fff', zIndex: 1000, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    toastClose: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16 },
    scannerModal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3000, background: '#000' },
};
